import React, { useContext, useEffect, useState } from 'react'
import { useApolloClient, useMutation } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { Header, Button, Message } from 'semantic-ui-react'
import moment from 'moment-timezone'
import { LOAD_ME_QUERY } from '../../graphql/user'
import { SCHEDULE_MEETING } from '../../graphql/schedule'
import { UPDATE_JOB_MUTATION } from '../../graphql/job'
import PublicProjectInfo from './PublicProjectInfo'
import General, { DEFAULT_GENERAL_INFO } from '../Forms/General'
import Professional, { DEFAULT_PROFESSIONAL_INFO } from '../Forms/Professional'
import MixpanelTracker from '../../utils/MixpanelTracker'
import MessageContext from '../../contexts/MessageContext'
import DataContext from '../../contexts/DataContext'
import ExpertProfile from '../../lib/ExpertProfile'

const ConfirmRegister = ({ project, jobData, setPage }) => {
  const history = useHistory()
  const apolloClient = useApolloClient()
  const { advisor, activeProjects } = useContext(DataContext)
  const { setAlertMsg } = useContext(MessageContext)

  const isExistingExpert = advisor.expert !== null

  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [profile, setProfile] = useState({
    ...DEFAULT_GENERAL_INFO,
    ...DEFAULT_PROFESSIONAL_INFO,
    first_name: advisor.firstName || '',
    last_name: advisor.lastName || '',
  })

  const [updateJob] = useMutation(UPDATE_JOB_MUTATION)
  const [scheduleMeeting] = useMutation(SCHEDULE_MEETING, {
    refetchQueries: [{ query: LOAD_ME_QUERY }],
  })

  useEffect(() => {
    MixpanelTracker.trackViewPublicProjectRegisterForm()
  }, [])

  const { meetingAt, qualificationResponses } = jobData

  const removeLocalStorage = () => {
    localStorage.removeItem('publicProject')
    localStorage.removeItem('qualification')
  }

  const completeRegister = (job, error) => {
    let state = null
    if (error) {
      setAlertMsg(error)
    }
    if (job) {
      state = {
        type: project.type,
        showNextStepsModal: activeProjects.length === 0,
        showPreInterviewModal: !!job.project.preInterviewLink,
        job,
      }
    }
    removeLocalStorage()
    history.push('/projects/active', state)
  }

  const createNewExpert = async () => {
    const profileInput = {
      ...profile,
      hourlyRate: parseInt(project.compensationType.params[0]),
    }
    const errorMessage = ExpertProfile.hasInvalidFields(profileInput)
    if (errorMessage) {
      setMessage(errorMessage)
      return false
    }

    try {
      await ExpertProfile.create(apolloClient, profileInput)
      return true
    } catch (err) {
      setMessage(
        err.message ||
          'Something went wrong. Please try again or contact us at help@dexterexperts.com'
      )
      return false
    }
  }

  const createJob = async () => {
    return new Promise((resovle, reject) => {
      const jobInput = {
        invite_status: 'Invited',
        invitedAt: new Date(),
        ...(qualificationResponses.length > 0 && {
          qualificationResponses,
        }),
      }
      if (project.type === 'Survey') {
        jobInput.response_status = 'Accepted'
        jobInput.overall_status = 'Scheduled'
        jobInput.responseAt = new Date()
      }
      updateJob({
        variables: {
          userId: advisor.id,
          projectId: project.id,
          job: jobInput,
        },
        context: { skipGlobalErrorHandling: true },
        onCompleted: ({ updateJob }) => {
          MixpanelTracker.trackPublicProjectLandingCreateJob(updateJob.user)
          if (project.type === 'Interview') {
            scheduleMeeting({
              variables: {
                jobId: updateJob.id,
                startTime: meetingAt,
              },
              onCompleted: (data) => resovle(data.scheduleMeeting),
            })
          } else {
            resovle(updateJob.user.jobs.find((el) => el.id === updateJob.id))
          }
        },
        onError: (err) => reject(err),
      })
    })
  }

  const getErrorMessage = (errMessage) => {
    let error = {
      title: 'Something went wrong. Failed to accept the invitation',
      content:
        'Please try again and contact us at help@dexterexperts.com if the issue persists.',
    }
    if (
      errMessage ===
        'This meeting could not be scheduled due to insufficient credits' ||
      errMessage === 'No more invites left'
    ) {
      error = {
        title: 'Failed to accept the invitation',
        content:
          'This project is currently on hold. Please try again in 24 hours and contact us at help@dexterexperts.com if the issue persists.',
      }
    }
    return error
  }

  const submit = async () => {
    if (loading) return
    setMessage(null)
    setLoading(true)
    try {
      if (!isExistingExpert) {
        const expertCreated = await createNewExpert()
        if (!expertCreated) {
          return
        }
      }
      const newJob = await createJob()
      completeRegister(newJob, null)
    } catch (err) {
      const error = getErrorMessage(err.message)
      completeRegister(null, error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div id="public-project-landing">
      <div className="content-block">
        Please review the details below and hit &quot;Submit&quot; to{' '}
        {project.type === 'Interview'
          ? ' schedule your meeting'
          : ' book your engagement'}
      </div>
      <div className="content-block">
        <Header size="medium">{project.title}</Header>
        {project.type === 'Interview' && (
          <Header size="small">
            {moment.tz(meetingAt, advisor.timeZone).format('llll z')}
          </Header>
        )}
        <PublicProjectInfo
          project={project}
          qualificationResponses={qualificationResponses}
        />
      </div>
      {!isExistingExpert && (
        <div className="content-block">
          {message && (
            <Message error header="Empty fields detected!" content={message} />
          )}
          <Header sub>About you</Header>
          <div className="form">
            <span>
              {`
                Review and submit your details to confirm your ${
                  project.type === 'Interview' ? 'call' : 'engagement'
                }. Your information is used to confirm your fit for this project and isn't shared publicly.
              `}
            </span>
            <Message color="blue">
              You&apos;re joining as{' '}
              <Message.Header
                style={{ display: 'inline-block', fontSize: '1rem' }}
              >
                {advisor.email}
              </Message.Header>
            </Message>
            <General
              skipTimeZone={true}
              skipHeader={true}
              profile={profile}
              setProfile={setProfile}
            />
            <Professional
              skipHourlyRate={true}
              skipHeader={true}
              profile={profile}
              setProfile={setProfile}
            />
          </div>
        </div>
      )}
      <div
        className={`public-project-form-btn ${
          project.type === 'Interview' ? 'double-btn' : 'single-btn'
        }`}
      >
        {project.type === 'Interview' && (
          <Button
            onClick={() => setPage('SCHEDULE')}
            id="back"
            icon="chevron left"
            loading={loading}
            disabled={loading}
          />
        )}
        <Button
          primary
          onClick={submit}
          loading={loading}
          disabled={loading}
          id="schedule"
        >
          Submit
        </Button>
      </div>
    </div>
  )
}

export default ConfirmRegister
